import { useDroppable } from "@dnd-kit/core";
import { Header } from "@tanstack/react-table";
import classNames from "classnames";
import { useCallback } from "react";

import { useTableContext } from "../context";
import { GRID_CELL_FOCUS_CLASSES } from "../grid-classes";
import TableColumnHeader from "./cell";

export default function TableColumnHeaderWrapper({
  header,
}: {
  header: Header<unknown, unknown>;
}) {
  const { setNodeRef } = useDroppable({
    id: header.id,
  });
  const { columnWidths, onColumnResize, sortableFields, tableId } =
    useTableContext();
  const width = columnWidths[header.id];
  const sortDirection = header.column.getIsSorted();
  const isSortable =
    !!header.column.getCanSort() &&
    !!sortableFields?.includes(header.column.id);
  const ariaSort = (() => {
    if (!isSortable) return undefined;
    if (sortDirection === "asc") return "ascending";
    if (sortDirection === "desc") return "descending";
    return "none";
  })();

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      let prevX = e.clientX;

      const onPointerMove = (ev: PointerEvent) => {
        const delta = ev.clientX - prevX;
        prevX = ev.clientX;
        onColumnResize(header.id, delta);
      };

      const onPointerUp = () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [header.id, onColumnResize],
  );

  return (
    <th
      id={`${tableId}-${header.id}`}
      ref={setNodeRef}
      scope="col"
      aria-sort={ariaSort}
      className={classNames(
        "h-target py-2 px-4 pr-target-min whitespace-nowrap text-ellipsis overflow-hidden relative border-r border-divider last:border-r-0",
        GRID_CELL_FOCUS_CLASSES,
      )}
      align="left"
      style={{ width }}
    >
      <TableColumnHeader
        key={header.id}
        header={header}
        isSortable={isSortable}
      />
      <div
        onPointerDown={handlePointerDown}
        aria-hidden="true"
        data-target-gap="column-resize"
        className="absolute top-0 right-0 h-full w-target-min cursor-col-resize group z-10"
      >
        <div className="absolute top-0 right-0 h-full w-0.5 group-hover:bg-primary group-active:bg-primary-hover transition-colors" />
      </div>
    </th>
  );
}
